/**
 * Pinia store for Complaint management
 *
 * Uses dedicated BE API (api.complaints) for all CRUD operations.
 * Manages processStatus transitions on the rental case when complaints open/close.
 */

import { defineStore } from 'pinia'
import { useCaseStore } from './caseStore'
import {
  type Complaint,
  type ComplaintDetailItem,
  type CreateComplaintRequest,
  hasOpenComplaint
} from '~/api/types/complaint'
import { resolveProcessStatus } from '~/utils/processStatus'

interface UserOption {
  userId: string
  displayName: string
}

/**
 * Parse customData from BE response (may be JSON string or object)
 */
const parseCustomData = (customData: unknown): Record<string, unknown> => {
  if (customData && typeof customData === 'string') {
    try { return JSON.parse(customData) } catch { return {} }
  }
  return (customData as Record<string, unknown>) || {}
}

/**
 * Map BE response to Complaint entity
 */
const mapComplaint = (raw: Record<string, unknown>): Complaint => ({
  id: raw.id as string,
  complaintNumber: raw.complaintNumber as string | undefined,
  rentalCaseId: raw.rentalCaseId as string,
  cause: raw.cause as Complaint['cause'],
  description: (raw.description as string) || '',
  liquidatorId: raw.liquidatorId as string,
  liquidatorName: (raw.liquidatorName as string) || '',
  status: raw.status as Complaint['status'],
  closedAt: raw.closedAt as number | undefined,
  closedBy: raw.closedBy as string | undefined,
  customData: parseCustomData(raw.customData),
  created: raw.created as number | undefined,
  changed: raw.changed as number | undefined,
  createdBy: raw.createdBy as string | undefined,
  changedBy: raw.changedBy as string | undefined,
  ownedBy: raw.ownedBy as string | undefined
})

/**
 * Map BE detailed response item
 */
const mapDetailItem = (item: Record<string, unknown>): Complaint => {
  const complaint = item.complaint as Record<string, unknown> | undefined
  return mapComplaint(complaint || item)
}

export const useComplaintStore = defineStore({
  id: 'complaints',

  state: () => ({
    complaints: {} as Record<string, Complaint>,
    /** All SDE users available as liquidator options */
    users: [] as UserOption[],
    usersLoading: false,
    usersInitialized: false,
    loading: false,
    initialized: false
  }),

  getters: {
    allComplaints: (state) => Object.values(state.complaints),

    openComplaints(): Complaint[] {
      return this.allComplaints.filter(c => c.status === 'OPEN')
    },

    closedComplaints(): Complaint[] {
      return this.allComplaints.filter(c => c.status === 'CLOSED')
    },

    openCount(): number {
      return this.openComplaints.length
    },

    /** Complaints for a specific rental case */
    complaintsForCase(): (rentalCaseId: string) => Complaint[] {
      return (rentalCaseId: string) =>
        this.allComplaints.filter(c => c.rentalCaseId === rentalCaseId)
    }
  },

  actions: {
    // ==================== Fetch ====================

    async fetchComplaints(api: API) {
      if (!api?.complaints) {
        console.warn('[complaintStore] api.complaints not available')
        return
      }

      this.loading = true
      try {
        const response = await api.complaints.getAllComplaints()
        const items = (response as unknown as { value: Record<string, unknown>[] }).value || []

        const newComplaints: Record<string, Complaint> = {}
        for (const item of items) {
          const complaint = mapComplaint(item)
          newComplaints[complaint.id] = complaint
        }
        this.complaints = newComplaints
        this.initialized = true
      } catch (e) {
        console.error('[complaintStore] Failed to fetch complaints:', e)
      } finally {
        this.loading = false
      }
    },

    async getDetailed(api: API, id: string): Promise<Complaint | null> {
      if (!api?.complaints) return null
      const response = await api.complaints.getComplaintDetailed(id)
      const complaint = mapDetailItem(response as Record<string, unknown>)
      this.complaints[complaint.id] = complaint
      return complaint
    },

    // ==================== User Fetching ====================

    async fetchUsers(api: API) {
      if (!api?.users) {
        console.warn('[complaintStore] api.users not available')
        return
      }
      if (this.usersInitialized) return

      this.usersLoading = true
      try {
        const result = await api.users.find({
          withTotalCount: false,
          offset: 0,
          limit: 200,
          ascending: true,
          orderBy: 'userDisplay'
        })

        const rawUsers = result.result || []
        this.users = rawUsers
          .filter(u => u.active !== false)
          .map(u => ({
            userId: u.id as string,
            displayName: u.display as string
          }))
        this.usersInitialized = true
      } catch (e) {
        console.error('[complaintStore] Failed to fetch users:', e)
      } finally {
        this.usersLoading = false
      }
    },

    // ==================== CRUD ====================

    /**
     * Create a new complaint for a rental case.
     * Also sets rental case processStatus to COMPLAINT.
     */
    async create(
      api: API,
      request: CreateComplaintRequest
    ): Promise<Complaint | null> {
      if (!api?.complaints) throw new Error('Complaints API not available')

      const caseStore = useCaseStore()
      const rentalCase = caseStore.getCase(request.rentalCaseId)
      if (!rentalCase) {
        console.error('[complaintStore] Rental case not found:', request.rentalCaseId)
        return null
      }

      // Guard: only one open complaint at a time
      const caseComplaints = this.complaintsForCase(request.rentalCaseId)
      if (hasOpenComplaint(caseComplaints)) {
        console.error('[complaintStore] Case already has an open complaint')
        return null
      }

      this.loading = true
      try {
        const response = await api.complaints.createComplaint(
          request.rentalCaseId,
          request.cause,
          request.description,
          request.liquidatorId,
          request.liquidatorName,
          'OPEN',
          null
        )

        const complaint = mapComplaint(response as Record<string, unknown>)
        this.complaints[complaint.id] = complaint

        // Set rental case processStatus to COMPLAINT
        const currentProcessStatus = resolveProcessStatus(rentalCase)
        await caseStore.update(api, rentalCase.id, {
          customData: {
            ...rentalCase.customData,
            preComplaintProcessStatus: currentProcessStatus,
            processStatus: 'COMPLAINT'
          }
        })

        try {
          const targetId = complaint.liquidatorId

          if (!targetId) {
            console.warn('[complaintStore] no user id, skipping notification')
          } else {
            await api.notifications.createNotificationEvent(
              'COMPLAINT_CREATED',
              targetId, 
              null,
              {
                header: 'Vytvorená nová reklamace',
                description: `Byla podána nová reklamace ${complaint.complaintNumber ?? complaint.id}.`,
                complaintId: complaint.id,
              },
            )
          }
        } catch (notifErr) {
          console.error('[complaintStore] Notification event failed (non-fatal):', notifErr)
        }

        return complaint
      } catch (e) {
        console.error('[complaintStore] Failed to create complaint:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    /**
     * Update a complaint (general save — status, description, etc.)
     */
    async update(
      api: API,
      id: string,
      updates: Partial<Complaint>
    ): Promise<Complaint | null> {
      if (!api?.complaints) throw new Error('Complaints API not available')

      const existing = this.complaints[id]
      if (!existing) {
        console.error('[complaintStore] Complaint not found:', id)
        return null
      }

      const merged = { ...existing, ...updates }

      this.loading = true
      try {
        await api.complaints.updateComplaint(
          id,
          merged.rentalCaseId,
          merged.cause,
          merged.description || '',
          merged.liquidatorId,
          merged.liquidatorName,
          merged.status,
          merged.closedAt || null,
          merged.closedBy || null,
          merged.customData ? { ...merged.customData } : null
        )

        // Fetch fresh from BE
        const fresh = await this.getDetailed(api, id)

        // If just closed, restore previous processStatus
        if (existing.status === 'OPEN' && merged.status === 'CLOSED') {
          const caseStore = useCaseStore()
          const rentalCase = caseStore.getCase(merged.rentalCaseId)
          if (rentalCase) {
            const previousStatus = rentalCase.customData?.preComplaintProcessStatus || 'ACTIVE'
            await caseStore.update(api, rentalCase.id, {
              customData: {
                ...rentalCase.customData,
                processStatus: previousStatus,
                preComplaintProcessStatus: undefined
              }
            })
          }
        }

        return fresh
      } catch (e) {
        console.error('[complaintStore] Failed to update complaint:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    /**
     * Close an open complaint. Convenience wrapper around update.
     */
    async close(
      api: API,
      id: string,
      closedBy?: string
    ): Promise<Complaint | null> {
      return this.update(api, id, {
        status: 'CLOSED',
        closedAt: Date.now(),
        closedBy
      })
    },

    /**
     * Delete a complaint (admin only).
     */
    async remove(api: API, id: string): Promise<boolean> {
      if (!api?.complaints) throw new Error('Complaints API not available')

      this.loading = true
      try {
        await api.complaints.deleteComplaint(id)
        delete this.complaints[id]
        return true
      } catch (e) {
        console.error('[complaintStore] Failed to delete complaint:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    // ==================== Lookups ====================

    getComplaint(id: string): Complaint | undefined {
      return this.complaints[id]
    },

    getComplaintsForCase(rentalCaseId: string): Complaint[] {
      return this.allComplaints.filter(c => c.rentalCaseId === rentalCaseId)
    },

    getOpenComplaintForCase(rentalCaseId: string): Complaint | undefined {
      return this.allComplaints.find(c => c.rentalCaseId === rentalCaseId && c.status === 'OPEN')
    }
  }
})

// Re-export types
export type {
  Complaint,
  ComplaintStatus,
  ComplaintCause,
  CreateComplaintRequest
} from '~/api/types/complaint'

export {
  complaintStatusLabels,
  complaintStatusColors,
  complaintCauseLabels,
  getOpenComplaint,
  hasOpenComplaint,
  getComplaintLabel
} from '~/api/types/complaint'
